import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AxiosError, isAxiosError } from 'axios';
import { authApiClient } from '../../../api/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { normalizeSftpSettings } from './sftpSettingsNormalize';

export { normalizeSftpSettings };

export const fetchSftpSettings = async () => {
  const { data } = await authApiClient.get('/organization/settings');
  return normalizeSftpSettings(data?.sftp_settings);
};

export const fetchSftpCredentials = async () => {
  const { data } = await authApiClient.get('/organization/sftp-credentials');
  return data;
};

/** Partial PATCH — only include keys present on the argument. */
export const updateSftpSettings = async (sftp_settings) => {
  const payload = {};
  if (Object.prototype.hasOwnProperty.call(sftp_settings, 'enabled')) {
    payload.enabled = Boolean(sftp_settings.enabled);
  }
  if (Object.prototype.hasOwnProperty.call(sftp_settings, 'poll_interval_seconds')) {
    payload.poll_interval_seconds = Number(sftp_settings.poll_interval_seconds);
  }
  if (Object.prototype.hasOwnProperty.call(sftp_settings, 'max_concurrent_files')) {
    payload.max_concurrent_files = Number(sftp_settings.max_concurrent_files);
  }
  if (Object.prototype.hasOwnProperty.call(sftp_settings, 'received')) {
    payload.received = Array.isArray(sftp_settings.received) ? sftp_settings.received : [];
  }
  const { data } = await authApiClient.patch('/organization/settings/sftp-settings', {
    sftp_settings: payload,
  });
  return normalizeSftpSettings(data?.sftp_settings);
};

export const upsertSftpCredentials = async ({ username, password }) => {
  const { data } = await authApiClient.put('/organization/sftp-credentials', {
    username,
    password: password || '',
  });
  return data;
};

export const saveSftpIngestionSettings = async ({ poll_interval_seconds, max_concurrent_files }) => {
  return updateSftpSettings({
    poll_interval_seconds,
    max_concurrent_files,
  });
};

export const saveSftpReceivedSettings = async (received) => {
  return updateSftpSettings({ received });
};

export const saveSftpConfiguration = async (payload) => {
  const { username, password, poll_interval_seconds, max_concurrent_files } = payload;

  if (!username) {
    throw new Error('SFTP username is required');
  }

  await upsertSftpCredentials({
    username,
    password: password || '',
  });

  await updateSftpSettings({
    poll_interval_seconds,
    max_concurrent_files,
  });

  return {
    username,
    poll_interval_seconds,
    max_concurrent_files,
  };
};

/** @deprecated use saveSftpConfiguration */
export const saveSftpConnection = saveSftpConfiguration;

export const useGetSftpSettingsHandler = () => {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: ['organization-sftp-settings'],
    queryFn: fetchSftpSettings,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  useEffect(() => {
    if (!query.isError) return;

    if (isAxiosError(query.error)) {
      dispatch(
        showSnackbar({
          message: query.error.response?.data?.errors?.message || query.error.message,
          severity: 'error',
        }),
      );
      return;
    }

    dispatch(
      showSnackbar({
        message: 'Failed to fetch SFTP settings',
        severity: 'error',
      }),
    );
  }, [dispatch, query.error, query.isError]);

  const normalized = query.data ?? {
    enabled: false,
    poll_interval_seconds: 60,
    max_concurrent_files: 2,
    received: [],
  };

  return {
    sftpSettings: normalized,
    enabled: Boolean(normalized.enabled),
    pollIntervalSeconds: normalized.poll_interval_seconds,
    maxConcurrentFiles: normalized.max_concurrent_files,
    received: normalized.received ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
  };
};

const invalidateSftpSettings = (queryClient) =>
  queryClient.invalidateQueries({ queryKey: ['organization-sftp-settings'] });

export const useSaveSftpReceivedSettingsHandler = ({ onSuccess } = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: saveSftpReceivedSettings,
    onSuccess: async (data) => {
      await invalidateSftpSettings(queryClient);
      await queryClient.refetchQueries({ queryKey: ['organization-sftp-settings'] });
      dispatch(
        showSnackbar({
          message: 'Received connector mapping saved',
          severity: 'success',
        }),
      );
      onSuccess && onSuccess(data);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.errors?.message || error.message,
            severity: 'error',
          }),
        );
        return;
      }

      dispatch(
        showSnackbar({
          message: 'Failed to save received connector mapping',
          severity: 'error',
        }),
      );
    },
  });

  return {
    onSubmit: (received) => mutation.mutate(received),
    isPending: mutation.isPending,
  };
};

export const useSaveSftpIngestionSettingsHandler = ({ onSuccess } = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: saveSftpIngestionSettings,
    onSuccess: async (data) => {
      await invalidateSftpSettings(queryClient);
      await queryClient.refetchQueries({ queryKey: ['organization-sftp-settings'] });
      dispatch(
        showSnackbar({
          message: 'SFTP ingestion settings saved',
          severity: 'success',
        }),
      );
      onSuccess && onSuccess(data);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.errors?.message || error.message,
            severity: 'error',
          }),
        );
        return;
      }

      dispatch(
        showSnackbar({
          message: 'Failed to save SFTP ingestion settings',
          severity: 'error',
        }),
      );
    },
  });

  return {
    onSubmit: (payload) => mutation.mutate(payload),
    isPending: mutation.isPending,
  };
};

export const useSaveSftpConnectionHandler = ({ onSuccess } = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: saveSftpConfiguration,
    onSuccess: async (data) => {
      await invalidateSftpSettings(queryClient);
      await queryClient.invalidateQueries({ queryKey: ['organization-sftp-credentials'] });
      dispatch(
        showSnackbar({
          message: 'SFTP configuration saved',
          severity: 'success',
        }),
      );
      onSuccess && onSuccess(data);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.errors?.message || error.message,
            severity: 'error',
          }),
        );
        return;
      }

      dispatch(
        showSnackbar({
          message: 'Failed to save SFTP configuration',
          severity: 'error',
        }),
      );
    },
  });

  return {
    onSubmit: (payload) => mutation.mutate(payload),
    isPending: mutation.isPending,
  };
};

export const useToggleSftpEnabledHandler = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (enabled) => updateSftpSettings({ enabled }),
    onSuccess: async (settings) => {
      await invalidateSftpSettings(queryClient);
      await queryClient.refetchQueries({ queryKey: ['organization-sftp-settings'] });
      dispatch(
        showSnackbar({
          message: settings.enabled ? 'SFTP enabled' : 'SFTP disabled',
          severity: 'success',
        }),
      );
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.errors?.message || error.message,
            severity: 'error',
          }),
        );
        return;
      }

      dispatch(
        showSnackbar({
          message: 'Failed to update SFTP enabled state',
          severity: 'error',
        }),
      );
    },
  });

  return {
    onToggle: (enabled) => mutation.mutate(enabled),
    isPending: mutation.isPending,
  };
};

export const buildSftpHomePath = (orgId) => {
  if (!orgId) return '';
  return `/srv/sftpgo/data/${orgId}`;
};

export const createEmptySftpConnection = () => ({
  sftp_username: '',
  sftp_password: '',
  poll_interval_seconds: '60',
  max_concurrent_files: '2',
});
